import { LoaderFunction } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import {
  ContentfulGetCertifications,
  ContentfulGetCertPreps,
  ContentfulGetCourses,
  ContentfulGetCatalogResourcesResponse,
  ContentfulGetLearningPaths,
  ContentfulGetRoles,
  ContentfulGetTopics,
  GET_CATALOG_RESOURCES,
  ContentfulGetLessons,
  ContentfulGetInstructorLedTraining,
} from 'src/graphql/queries/GetCatalogResources';
import { fetchGraphQL } from 'src/services/contentful/client';
import { GetCatalogResourcesQueryVariables } from 'src/graphql/generated/GetCatalogResources';
import { GetAllTaxonomyQueryVariables } from 'src/graphql/generated/GetAllTaxonomy';
import { ContentfulGetAllTaxonomyResponse, GET_ALL_TAXONOMY } from 'src/graphql/queries/GetAllTaxonomy';
import { sanitizeTaxonomy } from 'src/pages/CatalogPage/utils/loaderUtils';
import { isPreviewMode } from 'src/utils/isPreviewMode';
import {
  AtlenzCatalogResource,
  AtlenzContentType,
  AtlenzResponse,
  getAtlenzCatalogResources,
} from 'src/services/http/search';
import { ATLENZ_FF } from 'src/constants';

import { getCourseDuration, getLearningPathDuration, ResourceWithDurationAndType } from './utils/taxonomy';

export interface Taxonomy {
  duration: string[];
  learningLevel: string[];
  product: string[];
  productRole: string[];
  deployment: string[];
}

export interface CatalogLoaderProps {
  resources: ResourceWithDurationAndType[] | AtlenzCatalogResource[];
  taxonomy: Taxonomy;
}

const compareByTitle = (
  resourceA: ResourceWithDurationAndType | AtlenzCatalogResource,
  resourceB: ResourceWithDurationAndType | AtlenzCatalogResource,
  searchValue: string = '',
): number => {
  if (!searchValue) {
    return resourceA.title.localeCompare(resourceB.title, undefined, { sensitivity: 'base' });
  }

  const searchValueLower = searchValue.toLowerCase();
  const titleAMatches = resourceA.title.toLowerCase().includes(searchValueLower);
  const titleBMatches = resourceB.title.toLowerCase().includes(searchValueLower);

  if (titleAMatches && !titleBMatches) {
    return -1;
  }
  if (!titleAMatches && titleBMatches) {
    return 1;
  }

  return resourceA.title.localeCompare(resourceB.title, undefined, { sensitivity: 'base' });
};

export const getFiltersFromUrl = (url: string): Record<string, string[]> => {
  const queryParams: Record<string, string[]> = {};

  const knownFilterParams = new Set([
    // Default/General filters
    'type',
    'duration',
    'learningLevel',
    'search',
    'deployment',
    'product',
    'productRole',

    // Adobe-specific filters
    'loType',
    'loFormat',
    'learnerState',
    'catalog',
    'tags',
    'skills',
    'skillLevels',
  ]);

  const urlObj = new URL(url);
  const searchParams = new URLSearchParams(urlObj.search);

  searchParams.forEach((value, key) => {
    if (knownFilterParams.has(key)) {
      queryParams[key] = value.split(',');
    }
  });

  // product is the only valid BE filter for collection, collections are included only when:
  // - no filters are selected
  // - only product filter is selected
  // - only type filter is selected
  // - both product AND type filters are selected together

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const { search, ...filters } = queryParams;

  const filtersLength = Object.keys(filters).length;
  const hasProduct = !!queryParams.product;
  const hasType = !!queryParams.type;
  const hasOtherFilters = Object.keys(filters).some((key) => !['app', 'type', 'product'].includes(key));

  const shouldExcludeCollections =
    filtersLength > 0 &&
    (hasOtherFilters ||
      (hasProduct && hasType && filtersLength > 2) ||
      (hasProduct && !hasType && filtersLength > 1) ||
      (hasType && !hasProduct && filtersLength > 1));

  if (shouldExcludeCollections) {
    queryParams.collectionProduct = ['NOT_MATCHED'];
  } else {
    queryParams.collectionProduct = queryParams.product;
  }

  // lessons are included only when level filter is not selected
  const hasLevelFilter = !!queryParams.learningLevel;
  const shouldExcludeLessons = Object.keys(queryParams).length > 0 && hasLevelFilter;

  if (shouldExcludeLessons) {
    queryParams.lessonProduct = ['NOT_MATCHED'];
  } else {
    queryParams.lessonProduct = queryParams.product;
  }

  return queryParams;
};

export const loader: LoaderFunction = async ({ request }): Promise<CatalogLoaderProps> => {
  const preview = isPreviewMode();
  const { search, ...filters }: Record<string, string[]> = getFiltersFromUrl(request.url);
  const searchValue = search && search.length ? search[0] : '';
  const isAtlenzAvailable = FeatureGates.checkGate(ATLENZ_FF);

  const resources = isAtlenzAvailable
    ? await getAtlenzResources(filters, searchValue)
    : await getCatalogResources(preview, filters, searchValue);

  return {
    resources,
    taxonomy: await getAllTaxonomy(preview),
  };
};

export const getAtlenzResources = async (
  filters: Record<string, string[]>,
  searchValue: string = '',
  startResourceNumber: number = 0,
): Promise<AtlenzCatalogResource[]> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const { collectionProduct, lessonProduct, ...restOfFilters } = filters;
  const { results } = (await getAtlenzCatalogResources(
    restOfFilters,
    startResourceNumber,
    searchValue,
  )) as AtlenzResponse;

  const transformData = results.map(({ content }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
    const { productKeys, productPlatforms, subType, contentAri, ...restContent } = content;

    return {
      ...restContent,
      id: contentAri,
      product: content.productKeys[0] === 'NO_VALUE' || !content.productKeys.length ? null : [...content.productKeys],
      productRole: [],
      deployment: [...content.productPlatforms],
      type: subType ? AtlenzContentType[subType] : AtlenzContentType.lesson,
    };
  });

  return transformData.sort((a, b) =>
    compareByTitle(a as AtlenzCatalogResource, b as AtlenzCatalogResource, searchValue),
  ) as AtlenzCatalogResource[];
};

export const getCatalogResources = async (
  preview: boolean,
  filters: Record<string, string[]>,
  searchValue: string = '',
  skip: number = 0,
): Promise<ResourceWithDurationAndType[]> => {
  const response = await fetchGraphQL<ContentfulGetCatalogResourcesResponse, GetCatalogResourcesQueryVariables>(
    GET_CATALOG_RESOURCES,
    {
      preview,
      skip,
      searchValue,
      ...filters,
    },
  );

  const lessonsWithDuration = response.smLessonCollection?.items.map((lesson: ContentfulGetLessons) => {
    const { products, ...restParams } = lesson;

    return {
      ...restParams,
      product: products ? [...products] : null,
      type: lesson.__typename,
    };
  });

  const coursesWithDuration = response.smCourseCollection?.items.map((c: ContentfulGetCourses) => ({
    ...c,
    duration: getCourseDuration(c),
    type: c.__typename,
  }));

  const learningPathsWithDuration = response.learningPathCollection?.items.map((lp: ContentfulGetLearningPaths) => ({
    ...lp,
    duration: getLearningPathDuration(lp),
    type: lp.__typename,
  }));

  const certifications = response.certificationCollection?.items.map((cert: ContentfulGetCertifications) => ({
    ...cert,
    type: cert.__typename,
  }));

  const roles = response.roleCollection?.items.map((role: ContentfulGetRoles) => ({
    ...role,
    type: role.__typename,
  }));

  const topics = response.topicCollection?.items.map((topic: ContentfulGetTopics) => ({
    ...topic,
    type: topic.__typename,
  }));

  const certPreps = response.certificationPrepCollection?.items.map((certPrep: ContentfulGetCertPreps) => ({
    ...certPrep,
    type: certPrep.__typename,
  }));

  const instructorLedTrainingWithType = response.instructorLedTrainingCollection.items.map(
    (ilt: ContentfulGetInstructorLedTraining) => {
      // duration is optional for instructorLedTraining on contentful
      // if duration is null we exclude field to exclude resource
      // from filtering results when filtering by duration
      const { duration, ...iltWithoutDuration } = ilt;

      return {
        ...(duration ? ilt : iltWithoutDuration),
        type: ilt.__typename,
      };
    },
  );

  return [
    ...lessonsWithDuration,
    ...coursesWithDuration,
    ...learningPathsWithDuration,
    ...certifications,
    ...roles,
    ...topics,
    ...certPreps,
    ...instructorLedTrainingWithType,
  ].sort((a, b) =>
    compareByTitle(a as ResourceWithDurationAndType, b as ResourceWithDurationAndType, searchValue),
  ) as ResourceWithDurationAndType[];
};

const getAllTaxonomy = async (preview: boolean): Promise<Taxonomy> => {
  const response = await fetchGraphQL<ContentfulGetAllTaxonomyResponse, GetAllTaxonomyQueryVariables>(
    GET_ALL_TAXONOMY,
    { preview },
  );

  return sanitizeTaxonomy(response);
};
